import { createRouter, createWebHistory } from 'vue-router';
import SideLayout from '@/components/layouts/Side';
import Login from '@/components/views/Login';
import Settings from '@/components/views/user/Settings.vue';
import Security from '@/components/views/user/Security.vue';
import Subscription from '@/components/views/user/Subscription';
import AuthCallback from '@/components/views/AuthCallback';
import Onboarding from '@/components/views/Onboarding';
import Projects from '@/components/views/project/Projects';
import { FEATURES } from '@/constants';
import Help from '@/components/views/Help.vue';
import Dashboard from '@/components/views/Dashboard.vue';
import AdminDashboard from '@/components/views/admin/AdminDashboard.vue';
import ProjectFlow from '@/components/views/project/Flow.vue';
import ProjectAgent from '@/components/views/project/AgentDesignerView.vue';
import ProjectEndpoints from '@/components/views/project/Endpoints';
import ProjectVariables from '@/components/views/project/Variables';
import ProjectSecrets from '@/components/views/project/Secrets';
import ProjectTemplates from '@/components/views/project/Templates';
import ProjectSettingsWrapper from '@/components/views/project/settings/SettingsWrapper.vue';
import ProjectAuthentication from '@/components/views/project/Authentication';
import ProjectAuthenticationConfig from '@/components/views/project/AuthenticationConfig';
import ProjectTeamMembers from '@/components/views/project/settings/TeamMembers.vue';
import ProjectUsers from '@/components/views/project/settings/Users.vue';
import ProjectUIFeatures from '@/components/views/project/settings/UIFeatures.vue';
import ProjectEncryptionKeys from '@/components/views/project/settings/EncryptionKeys.vue';
// import ProjectDataAdapters from '@/components/views/project/data-adapters/DataAdapters.vue';
import ProjectDataAdapter from '@/components/views/project/data-adapters/DataAdapter.vue';
import ProjectSettings from '@/components/views/project/settings/Settings.vue';
import ProjectPersonas from '@/components/views/project/Personas';
// import ProjectAIProviders from '@/components/views/project/AIProviders';
import ProjectEmailProviders from '@/components/views/project/EmailProviders.vue';
import ProjectDataTypes from '@/components/views/project/DataTypes';
import ProjectFlows from '@/components/views/project/Flows';
import ProjectMonitoring from '@/components/views/project/Monitoring.vue';
import ProjectDataAdapterSettings from '@/components/views/project/data-adapters/DataAdapterSettings.vue';
import ProjectDataAdapterTools from '@/components/views/project/data-adapters/DataAdapterTools.vue';
import ProjectPublishing from '@/components/views/project/Publish';
import ProjectIntegrationHub from '@/components/views/project/IntegrationHub.vue';
import Users from '@/components/views/admin/Users.vue';
import AdminSecurity from '@/components/views/admin/Security.vue';
import AcceptInvite from '@/components/views/AcceptInvite.vue';

let store;

const router = createRouter({
  history: createWebHistory(),
  base: '/',
  routes: [
    {
      path: '/',
      component: Dashboard,
      meta: { requiresAuth: true },
    },
    {
      path: '/help/:id',
      name: 'help',
      component: Help,
      meta: { requiresAuth: true },
    },
    {
      path: '/onboarding',
      name: 'onboarding',
      component: Onboarding,
      meta: { requiresAuth: true },
    },
    {
      path: '/user',
      component: SideLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'subscriptions',
          name: 'subscriptions',
          component: Subscription,
          meta: { requiresAuth: true },
        },
        {
          path: 'settings',
          name: 'settings',
          component: Settings,
          meta: { requiresAuth: true },
        },
        {
          path: 'security',
          name: 'security',
          component: Security,
          meta: { requiresAuth: true },
        },
      ],
    },
    {
      path: '/project',
      name: 'project',
      component: Projects,
      meta: { requiresAuth: true },
    },
    {
      path: '/project/:projectId',
      name: 'project-parent',
      component: SideLayout,
      meta: { requiresAuth: true, header: 'no_code' },
      children: [
        {
          path: '/project/:projectId/endpoints',
          name: 'project-endpoints',
          component: ProjectEndpoints,
        },
        {
          path: '/project/:projectId/variables',
          name: 'project-variables',
          component: ProjectVariables,
        },
        {
          path: '/project/:projectId/secrets',
          name: 'project-secrets',
          component: ProjectSecrets,
        },
        {
          path: '/project/:projectId/templates',
          name: 'project-templates',
          component: ProjectTemplates,
        },
        {
          path: '/project/:projectId/authentication',
          name: 'project-authentication',
          component: ProjectAuthentication,
        },
        {
          path: '/project/:projectId/settings',
          component: ProjectSettingsWrapper,
          children: [
            {
              path: 'settings',
              name: 'project-settings',
              component: ProjectSettings,
              meta: { parentRouteName: 'project-settings' },
            },
            {
              path: 'authentication',
              name: 'project-authentication-config',
              component: ProjectAuthenticationConfig,
              meta: { parentRouteName: 'project-settings' },
            },
            {
              path: 'team-members',
              name: 'project-team-members',
              component: ProjectTeamMembers,
              meta: { parentRouteName: 'project-settings' },
            },
            {
              path: 'users',
              name: 'project-users',
              component: ProjectUsers,
              meta: { parentRouteName: 'project-settings' },
            },
            {
              path: 'ui-features',
              name: 'project-ui-features',
              component: ProjectUIFeatures,
              meta: { parentRouteName: 'project-settings' },
            },
            {
              path: 'encryption-keys',
              name: 'project-encryption-keys',
              component: ProjectEncryptionKeys,
              meta: { parentRouteName: 'project-settings' },
            },
          ],
        },
        {
          path: '/project/:projectId/publishing',
          name: 'project-publishing',
          component: ProjectPublishing,
        },
        {
          path: '/project/:projectId/integration-hub',
          name: 'project-integration-hub',
          component: ProjectIntegrationHub,
        },
        // {
        //   path: '/project/:projectId/data-adapters',
        //   name: 'project-data-adapters',
        //   component: ProjectDataAdapters,
        // },
        {
          path: '/project/:projectId/integration-hub/data-adapter',
          component: ProjectDataAdapter,
          children: [
            {
              path: ':dataAdapter/settings',
              name: 'project-data-adapter',
              component: ProjectDataAdapterSettings,
              meta: { parentRouteName: 'project-integration-hub' },
            },
            {
              path: ':dataAdapter/tools',
              name: 'project-data-adapter-tools',
              component: ProjectDataAdapterTools,
              meta: { parentRouteName: 'project-integration-hub' },
            },
          ],
        },
        {
          path: '/project/:projectId/personas',
          name: 'project-personas',
          component: ProjectPersonas,
        },
        {
          path: '/project/:projectId/email-providers',
          name: 'project-email-providers',
          component: ProjectEmailProviders,
        },
        {
          path: '/project/:projectId/datatypes',
          name: 'project-datatypes',
          component: ProjectDataTypes,
        },
        {
          path: '/project/:projectId/flow',
          name: 'project-flows',
          component: ProjectFlows,
        },
        {
          path: '/project/:projectId/flow/:flowId',
          name: 'project-flow',
          component: ProjectFlow,
          meta: { parentRouteName: 'project-flows' },
        },
        {
          path: '/project/:projectId/agent/:flowId',
          name: 'project-agent',
          component: ProjectAgent,
          meta: { parentRouteName: 'project-flows' },
        },
        {
          path: '/project/:projectId/monitoring',
          name: 'project-monitoring',
          component: ProjectMonitoring,
        },
      ],
    },
    {
      path: '/admin',
      name: 'admin',
      component: AdminDashboard,
      meta: { requiresAuth: true, feature: FEATURES.ADMIN_DASHBOARD },
    },
    {
      path: '/admin',
      component: SideLayout,
      meta: { requiresAuth: true, feature: FEATURES.ADMIN_DASHBOARD },
      children: [
        {
          path: 'users',
          name: 'admin-users',
          component: Users,
        },
        {
          path: 'security',
          name: 'admin-security',
          component: AdminSecurity,
        },
      ],
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
    },
    {
      path: '/passwordreset',
      name: 'password-reset',
      component: Login,
    },
    {
      path: '/register',
      name: 'register',
      component: Login,
    },
    {
      path: '/authcallback',
      name: 'authcallback',
      component: AuthCallback,
    },
    {
      path: '/acceptinvite',
      name: 'accept-invite',
      component: AcceptInvite,
    },
    // pathMatch is the name of the param, e.g., going to /not/found yields
    // { params: { pathMatch: ['not', 'found'] }}
    // this is thanks to the last *, meaning repeated params and it is necessary if you
    // plan on directly navigating to the not-found route using its name
    { path: '/:pathMatch(.*)*', name: 'not-found', redirect: '/login' },
    // if you omit the last `*`, the `/` character in params will be encoded when resolving or pushing
    { path: '/:pathMatch(.*)', name: 'bad-not-found', redirect: '/login' },
  ],
});

router.setStore = (vuexStore) => {
  store = vuexStore;
};

/**
 * Whether any record matched by the route requires a feature the current tenant lacks.
 * @param {import('vue-router').RouteLocationNormalized} route Route to check.
 * @returns {boolean} True when a required `meta.feature` is missing from the tenant's features.
 */
function isFeatureMissing(route) {
  return route.matched.some((record) => record.meta.feature && !store.state.features.includes(record.meta.feature));
}

/**
 * Re-applies the `meta.feature` gate to the current route once the tenant's features are loaded.
 * The initial navigation (e.g. a hard reload on /admin/users) resolves before bootstrap fetches the tenant,
 * so `beforeEach` cannot gate it, and the guard cannot wait for bootstrap either: bootstrap itself awaits
 * `router.isReady()`. Called by the store right after the tenant is set, before the app is marked bootstrapped.
 * @returns {Promise<void>} Resolves once any redirect to '/' has completed.
 */
router.enforceRouteFeatures = async () => {
  if (isFeatureMissing(router.currentRoute.value)) await router.replace('/');
};

router.beforeEach(async (to, from, next) => {
  // if (to.fullPath && to.query.redirect && to.path !== '/authcallback') {
  //   next(to.query.redirect);
  //   return;
  // }
  if (store.state.bootstrapped && to.matched.some((record) => record.meta.requiresAuth)) {
    if (!store.state.user || !store.state.user.email) {
      next({ path: '/login', params: { nextUrl: to.fullPath } });
      return;
    }
    const { users } = to.meta;
    if (users && users.length && !users.includes(store.state.user.email)) {
      // email not authorised so redirect to home page
      next('/');
      return;
    }

    if (isFeatureMissing(to)) {
      next({ path: '/' });
      return;
    }
  }

  next();
});

router.afterEach((to, from) => {
  if (from.name === 'dashboard' && to.name === 'conversation' && to.hash === '#dashboard') {
    to.meta.transition = 'slide-up';
  }
});

router.onError((error) => {
  if (/loading chunk \d* failed./i.test(error.message) && navigator.onLine) {
    window.location.reload();
  }
});

export default router;
